import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useDirectory from "../hooks/useDirectory";
import useVisiblePolling from "../hooks/useVisiblePolling";
import api, { errorMessage } from "../lib/api";
import { getInitials, avatarColorClass } from "../lib/avatar";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  STAGES, MANUAL_STAGES, STAGE_LABEL, INTERACTION_TYPES,
  prospectSourceLabel, formatRelativeDate, ACQUISITION_CHANNELS, ACQUISITION_CHANNEL_LABEL, parseAcquisitionChannels,
} from "../lib/pipeline";
import { PHASES, FLAGS, flagLabel, phaseOf, prospectsToPipeline } from "../lib/prospectViews";
import StatusExtraFields, { defaultStatusExtra, isStatusExtraValid, buildStatusExtraPayload } from "../components/StatusExtraFields";
import { PilotTag } from "../components/PilotBits";
import JamoraIcon from "../components/JamoraIcon";
import { PROSPECT_PILOT_FILTERS, matchesPilotFilter } from "../lib/pilots";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ResponsivePopover as Popover, ResponsivePopoverTrigger as PopoverTrigger, ResponsivePopoverContent as PopoverContent } from "../components/ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import {
  Search, Loader2, Upload, Plus, MoreHorizontal, Pencil, UserPlus, CircleDot,
  PhoneCall, ClipboardList, MapPin, Mail, Phone, ChevronLeft, ChevronRight,
  SlidersHorizontal, Trash2, X,
} from "lucide-react";
import { FilterField } from "../components/FilterField";
import useCustomerBadges from "../hooks/useCustomerBadges";
import { CustomerMark } from "../components/customers/CustomerBits";
import { ProspectCard } from "../components/prospects/ProspectCard";
import TaskFormSheet from "../components/TaskFormSheet";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";

const SKIPPED_LABELS = { late: "À faire aujourd'hui ou en retard", noaction: "Aucune action visible" };
const PAGE_SIZE = 10;

const EMPTY_EDIT_FORM = {
  pharmacy_name: "", phone: "", email: "", region: "", commune: "",
  reference_name: "", reference_function: "", reference_phone: "",
};

export default function Prospects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, "prospects:edit");
  const canAssign = hasPermission(user, "prospects:assign");
  const canScopeAllTasks = hasPermission(user, "tasks:scope_all");
  const directory = useDirectory(canAssign || canScopeAllTasks);
  const canChangeStatus = hasPermission(user, "prospects:change_status");
  const canAddInteraction = hasPermission(user, "prospects:add_interaction");
  const canCreateTasks = hasPermission(user, "tasks:create");
  const canCreateProspects = hasPermission(user, "prospects:create");
  const canImportCsv = hasPermission(user, "csv:import");
  const canDelete = hasPermission(user, "prospects:delete");
  const [deleting, setDeleting] = useState(null);
  const hasAnyRowAction = canEdit || canAssign || canChangeStatus || canAddInteraction || canCreateTasks;

  const [items, setItems] = useState([]);
  const customerBadges = useCustomerBadges(user);
  const [loading, setLoading] = useState(true);
  // Filters live in the address (Pipeline proposal D-05): « Retour » restores them and a view can be shared or sent from the Pipeline.
  const [searchParams, setSearchParams] = useSearchParams();
  const setParam = (key, value) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (!value || value === "all") next.delete(key); else next.set(key, value);
    next.delete("skipped");
    return next;
  }, { replace: true });
  const PILOT_URL = { pilots: "yes", none: "no", running: "running" };
  const PILOT_FROM_URL = { yes: "pilots", no: "none", running: "running" };
  const search = searchParams.get("q") || "";
  const setSearch = (v) => setParam("q", v);
  const statusFilter = STAGES.some((s) => s.id === searchParams.get("status")) ? searchParams.get("status") : "all";
  const setStatusFilter = (v) => setParam("status", v);
  const regionFilter = searchParams.get("region") || "all";
  const setRegionFilter = (v) => setParam("region", v);
  const commercialFilter = searchParams.get("assignee") || "all";
  const setCommercialFilter = (v) => setParam("assignee", v);
  const sourceFilter = searchParams.get("source") || "all";
  const setSourceFilter = (v) => setParam("source", v);
  const pilotFilter = PILOT_FROM_URL[searchParams.get("pilot")] || "all";
  const setPilotFilter = (v) => setParam("pilot", PILOT_URL[v] || null);
  const channelFilter = searchParams.get("channel") || "all";
  const setChannelFilter = (v) => setParam("channel", v);
  const phaseFilter = PHASES.some((ph) => ph.id === searchParams.get("phase")) ? searchParams.get("phase") : "all";
  const setPhaseFilter = (v) => setParam("phase", v);
  const dormantOnly = searchParams.get("flag") === "dormant";
  const setDormantOnly = (v) => setParam("flag", v ? "dormant" : null);
  const skipped = (searchParams.get("skipped") || "").split(",").filter(Boolean);
  const [page, setPage] = useState(1);

  const [editingProspect, setEditingProspect] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [savingEdit, setSavingEdit] = useState(false);

  const [assigningProspect, setAssigningProspect] = useState(null);
  const [assignUserId, setAssignUserId] = useState("__none__");
  const [savingAssign, setSavingAssign] = useState(false);

  const [statusProspect, setStatusProspect] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusExtra, setStatusExtra] = useState({});
  const [savingStatus, setSavingStatus] = useState(false);

  const [interactionProspect, setInteractionProspect] = useState(null);
  const [interactionKind, setInteractionKind] = useState("call");
  const [interactionContent, setInteractionContent] = useState("");
  const [savingInteraction, setSavingInteraction] = useState(false);

  const [taskProspect, setTaskProspect] = useState(null);

  // `silent`: background refresh (a colleague changed a prospect) — no loading state, a failed attempt is simply retried next time.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (search) params.search = search;
    try {
      const { data } = await api.get("/prospects", { params });
      setItems(data);
    } catch (e) {
      if (!silent) throw e;
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);
  useVisiblePolling(() => { if (!interactionProspect && !taskProspect) load({ silent: true }); }, 20000);


  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, regionFilter, commercialFilter, sourceFilter, channelFilter, phaseFilter, dormantOnly, pilotFilter]);

  const userName = (id) => {
    if (!id) return null;
    if (id === user?.id) return user.name;
    return directory.find((u) => u.id === id)?.name || null;
  };

  const regions = useMemo(
    () => [...new Set(items.map((p) => p.region).filter(Boolean))].sort(),
    [items],
  );
  const commercials = useMemo(
    () => [...new Set(items.map((p) => p.assigned_to).filter(Boolean))],
    [items],
  );
  const sources = useMemo(
    () => [...new Set(items.map((p) => p.source).filter(Boolean))],
    [items],
  );

  const filtered = useMemo(() => items.filter((p) => {
    if (regionFilter !== "all" && p.region !== regionFilter) return false;
    if (commercialFilter !== "all") {
      if (commercialFilter === "__none__" ? p.assigned_to : p.assigned_to !== commercialFilter) return false;
    }
    if (sourceFilter !== "all" && p.source !== sourceFilter) return false;
    if (!matchesPilotFilter(p, pilotFilter)) return false;
    if (channelFilter !== "all" && !parseAcquisitionChannels(p.acquisition_channel).includes(channelFilter)) return false;
    if (phaseFilter !== "all" && !phaseOf(phaseFilter).stageIds.includes(p.status)) return false;
    if (dormantOnly && !p.dormant) return false;
    return true;
  }), [items, regionFilter, commercialFilter, sourceFilter, pilotFilter, channelFilter, phaseFilter, dormantOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => setSearchParams({}, { replace: true });
  const hasActiveFilters = search || statusFilter !== "all" || regionFilter !== "all"
    || commercialFilter !== "all" || sourceFilter !== "all" || pilotFilter !== "all"
    || channelFilter !== "all" || phaseFilter !== "all" || dormantOnly;
  const toPipeline = prospectsToPipeline({
    q: search || undefined, status: statusFilter !== "all" ? statusFilter : undefined, region: regionFilter !== "all" ? regionFilter : undefined,
    assignee: commercialFilter !== "all" ? commercialFilter : undefined, source: sourceFilter !== "all" ? sourceFilter : undefined,
    pilot: searchParams.get("pilot") || undefined, channel: channelFilter !== "all" ? channelFilter : undefined,
    phase: phaseFilter !== "all" ? phaseFilter : undefined, flag: dormantOnly ? "dormant" : undefined,
  }, user?.id);

  // Mobile-only removable chips: desktop already shows the active value
  // directly inside each visible Select.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (search) chips.push({ id: "search", label: `Recherche : ${search}`, onRemove: () => setSearch("") });
    if (statusFilter !== "all") {
      chips.push({ id: "status", label: STAGE_LABEL[statusFilter] || statusFilter, onRemove: () => setStatusFilter("all") });
    }
    if (regionFilter !== "all") {
      chips.push({ id: "region", label: regionFilter, onRemove: () => setRegionFilter("all") });
    }
    if (commercialFilter !== "all") {
      const label = commercialFilter === "__none__" ? "Non assigné" : (userName(commercialFilter) || "Utilisateur");
      chips.push({ id: "commercial", label, onRemove: () => setCommercialFilter("all") });
    }
    if (sourceFilter !== "all") {
      chips.push({ id: "source", label: prospectSourceLabel(sourceFilter), onRemove: () => setSourceFilter("all") });
    }
    if (pilotFilter !== "all") {
      chips.push({ id: "pilot", label: PROSPECT_PILOT_FILTERS.find((f) => f.id === pilotFilter)?.label, onRemove: () => setPilotFilter("all") });
    }
    if (channelFilter !== "all") chips.push({ id: "channel", label: `Canal : ${ACQUISITION_CHANNEL_LABEL[channelFilter] || channelFilter}`, onRemove: () => setChannelFilter("all") });
    if (phaseFilter !== "all") chips.push({ id: "phase", label: phaseOf(phaseFilter).label, onRemove: () => setPhaseFilter("all") });
    if (dormantOnly) chips.push({ id: "dormant", label: flagLabel("dormant"), onRemove: () => setDormantOnly(false) });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, regionFilter, commercialFilter, sourceFilter, pilotFilter, channelFilter, phaseFilter, dormantOnly, directory, user]);

  // --- Modifier ---
  const openEdit = (p) => {
    setEditForm({
      pharmacy_name: p.pharmacy_name || "",
      phone: p.phone || "",
      email: p.email || "",
      region: p.region || "",
      commune: p.commune || "",
      reference_name: p.reference_name || "",
      reference_function: p.reference_function || "",
      reference_phone: p.reference_phone || "",
    });
    setEditingProspect(p);
  };
  const saveEdit = async () => {
    if (!editForm.pharmacy_name.trim()) {
      toast.error("Le nom de la pharmacie est requis");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/prospects/${editingProspect.id}`, editForm);
      toast.success("Prospect modifié");
      setEditingProspect(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'enregistrement"));
    } finally {
      setSavingEdit(false);
    }
  };

  // --- Assigner ---
  const openAssign = (p) => {
    setAssignUserId(p.assigned_to || "__none__");
    setAssigningProspect(p);
  };
  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await api.post(`/prospects/${assigningProspect.id}/assign`, {
        user_id: assignUserId === "__none__" ? null : assignUserId,
      });
      toast.success("Prospect assigné");
      setAssigningProspect(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'assignation"));
    } finally {
      setSavingAssign(false);
    }
  };

  // --- Changer le statut ---
  const openStatus = (p) => {
    setNewStatus(p.status);
    setStatusExtra(defaultStatusExtra(p.status));
    setStatusProspect(p);
  };
  const selectNewStatus = (status) => {
    setNewStatus(status);
    setStatusExtra(defaultStatusExtra(status));
  };
  const saveStatus = async () => {
    if (!isStatusExtraValid(newStatus, statusExtra)) {
      toast.error("Merci de compléter les informations requises.");
      return;
    }
    setSavingStatus(true);
    try {
      await api.post(`/prospects/${statusProspect.id}/status`, {
        status: newStatus,
        ...buildStatusExtraPayload(newStatus, statusExtra),
      });
      toast.success("Statut mis à jour");
      setStatusProspect(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors du changement de statut"));
    } finally {
      setSavingStatus(false);
    }
  };

  // --- Ajouter une interaction ---
  const openInteraction = (p) => {
    setInteractionKind("call");
    setInteractionContent("");
    setInteractionProspect(p);
  };
  const saveInteraction = async () => {
    if (!interactionContent.trim()) {
      toast.error("Le compte-rendu ne peut pas être vide");
      return;
    }
    setSavingInteraction(true);
    try {
      await api.post(`/prospects/${interactionProspect.id}/interactions`, {
        kind: interactionKind, content: interactionContent,
      });
      toast.success("Interaction ajoutée");
      setInteractionProspect(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'ajout"));
    } finally {
      setSavingInteraction(false);
    }
  };

  // --- Créer une tâche : the shared task form, locked on this prospect ---
  const openTask = (p) => setTaskProspect(p);

  // Same RBAC-gated action list for the desktop row's DropdownMenu and the
  // mobile card's, defined once so the two can't drift apart.
  const prospectActionItems = (p) => (
    <>
      {canEdit && (
        <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`action-edit-${p.id}`}>
          <Pencil size={14} className="mr-2" /> Modifier
        </DropdownMenuItem>
      )}
      {canAssign && (
        <DropdownMenuItem onClick={() => openAssign(p)} data-testid={`action-assign-${p.id}`}>
          <UserPlus size={14} className="mr-2" /> Assigner à…
        </DropdownMenuItem>
      )}
      {canChangeStatus && (
        <DropdownMenuItem onClick={() => openStatus(p)} data-testid={`action-status-${p.id}`}>
          <CircleDot size={14} className="mr-2" /> Changer le statut
        </DropdownMenuItem>
      )}
      {(canAddInteraction || canCreateTasks) && (canEdit || canAssign || canChangeStatus) && (
        <DropdownMenuSeparator />
      )}
      {canAddInteraction && (
        <DropdownMenuItem onClick={() => openInteraction(p)} data-testid={`action-interaction-${p.id}`}>
          <PhoneCall size={14} className="mr-2" /> Ajouter une interaction
        </DropdownMenuItem>
      )}
      {canCreateTasks && (
        <DropdownMenuItem onClick={() => openTask(p)} data-testid={`action-task-${p.id}`}>
          <ClipboardList size={14} className="mr-2" /> Créer une tâche
        </DropdownMenuItem>
      )}
      {canDelete && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setDeleting(p)} className="text-red-600 focus:text-red-700" data-testid={`action-delete-${p.id}`}>
            <Trash2 size={14} className="mr-2" /> Supprimer
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const confirmDelete = async (reason) => {
    const target = deleting;
    try {
      await api.delete(`/prospects/${target.id}`, { data: { reason } });
      setItems((list) => list.filter((i) => i.id !== target.id));
      toast.success("Prospect mis à la corbeille");
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer ce prospect."));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="prospects-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Prospects</h1>
          <p className="text-sm text-slate-500">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</p>
          <Link to={`/pipeline?${toPipeline.toString()}`} className="text-sm text-jamora hover:underline" data-testid="link-see-in-pipeline">Voir dans le Pipeline</Link>
        </div>
        {/* Import CSV / Nouveau prospect are desktop-only in-page buttons: both
            are already permission-filtered shortcuts in the header's "+"
            QuickActionsMenu, which stays reachable on mobile. */}
        <div className="hidden md:flex gap-2">
          {canImportCsv && (
            <Button variant="outline" onClick={() => navigate("/import")} data-testid="btn-prospects-import">
              <Upload size={15} className="mr-1.5" /> Importer (CSV)
            </Button>
          )}
          {canCreateProspects && (
            <Button
              onClick={() => navigate("/prospects/nouveau")}
              data-testid="btn-prospects-new"
            >
              <Plus size={16} className="mr-1.5" /> Nouveau prospect
            </Button>
          )}
        </div>
      </div>

      {skipped.length > 0 && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800" role="status" data-testid="skipped-banner">
          <span>Le filtre du Pipeline « {skipped.map((k) => SKIPPED_LABELS[k] || FLAGS.find((f) => f.id === k)?.label || k).join(", ")} » n'existe pas dans la liste et n'est pas appliqué.</span>
          <button type="button" aria-label="Fermer ce message" onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete("skipped"); return n; }, { replace: true })} className="shrink-0 rounded p-1 hover:bg-sky-100"><X size={14} /></button>
        </div>
      )}

      {/* Compact mobile filter bar: search + a single "Filtres" popover
          reusing the same statusFilter/regionFilter/commercialFilter/
          sourceFilter state as desktop's always-visible selects below. */}
      <div className="space-y-2">
        <div className="flex items-center md:flex-wrap gap-2">
          <div className="relative flex-1 min-w-0 md:min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              data-testid="input-prospects-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, contact, région…"
              className="pl-9"
            />
          </div>

          <div className="hidden md:flex md:flex-wrap gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-region-filter">
                <SelectValue placeholder="Toutes les régions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les régions</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={commercialFilter} onValueChange={setCommercialFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-commercial-filter">
                <SelectValue placeholder="Tous les commerciaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les commerciaux</SelectItem>
                <SelectItem value="__none__">Non assigné</SelectItem>
                {commercials.map((id) => (
                  <SelectItem key={id} value={id}>{userName(id) || "Utilisateur"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pilotFilter} onValueChange={setPilotFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-pilot-filter">
                <SelectValue placeholder="Pilotes ou non" />
              </SelectTrigger>
              <SelectContent>
                {PROSPECT_PILOT_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-source-filter">
                <SelectValue placeholder="Toutes les sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sources</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s} value={s}>{prospectSourceLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-channel-filter">
                <SelectValue placeholder="Tous les canaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les canaux</SelectItem>
                {ACQUISITION_CHANNELS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="select-phase-filter">
                <SelectValue placeholder="Toutes les phases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les phases</SelectItem>
                {PHASES.filter((ph) => ph.id !== "all").map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={dormantOnly ? "default" : "outline"} aria-pressed={dormantOnly} onClick={() => setDormantOnly(!dormantOnly)} data-testid="btn-dormant-filter">
              Inactifs (14 j)
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={resetFilters} data-testid="btn-reset-filters">
                Réinitialiser
              </Button>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Filtres" data-testid="btn-prospects-filters-toggle">
                <SlidersHorizontal size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3 md:hidden">
              <div className="text-sm font-semibold text-slate-900">Filtres</div>
              <FilterField label="Statut">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="select-status-filter-mobile"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Région">
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger data-testid="select-region-filter-mobile"><SelectValue placeholder="Toutes les régions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les régions</SelectItem>
                    {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Commercial">
                <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                  <SelectTrigger data-testid="select-commercial-filter-mobile"><SelectValue placeholder="Tous les commerciaux" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les commerciaux</SelectItem>
                    <SelectItem value="__none__">Non assigné</SelectItem>
                    {commercials.map((id) => <SelectItem key={id} value={id}>{userName(id) || "Utilisateur"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Pilotes">
                <Select value={pilotFilter} onValueChange={setPilotFilter}>
                  <SelectTrigger data-testid="select-pilot-filter-mobile"><SelectValue placeholder="Pilotes ou non" /></SelectTrigger>
                  <SelectContent>
                    {PROSPECT_PILOT_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Source">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="select-source-filter-mobile"><SelectValue placeholder="Toutes les sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sources</SelectItem>
                    {sources.map((s) => <SelectItem key={s} value={s}>{prospectSourceLabel(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Canal">
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger data-testid="select-channel-filter-mobile"><SelectValue placeholder="Tous les canaux" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les canaux</SelectItem>
                    {ACQUISITION_CHANNELS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Phase">
                <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                  <SelectTrigger data-testid="select-phase-filter-mobile"><SelectValue placeholder="Toutes les phases" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les phases</SelectItem>
                    {PHASES.filter((ph) => ph.id !== "all").map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <Button variant={dormantOnly ? "default" : "outline"} size="sm" className="w-full" aria-pressed={dormantOnly} onClick={() => setDormantOnly(!dormantOnly)} data-testid="btn-dormant-filter-mobile">
                Inactifs (14 j)
              </Button>
              <Button
                variant="outline" size="sm" className="w-full"
                onClick={resetFilters} disabled={!hasActiveFilters} data-testid="btn-reset-filters-mobile"
              >
                Réinitialiser
              </Button>
            </PopoverContent>
          </Popover>
        </div>

        {/* Active filter chips, mobile only: desktop already shows the
            active value directly inside each visible Select. */}
        {activeFilterChips.length > 0 && (
          <div className="md:hidden flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id} type="button" onClick={chip.onRemove}
                data-testid={`chip-${chip.id}`}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-slate-200"
              >
                {chip.label} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Flattened on mobile (no border/shadow/rounded/bg): each prospect
          is already its own bordered card there, and wrapping them in another
          Card read as "a card inside a card". */}
      <Card className="p-0 overflow-hidden border-0 shadow-none rounded-none bg-transparent md:rounded-xl md:border md:shadow md:bg-card">
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-jamora" />
          </div>
        ) : filtered.length === 0 ? (
          <>
            {/* Desktop: unchanged single-line message. */}
            <div className="hidden md:block p-12 text-center text-slate-500 text-sm">Aucun prospect ne correspond à ces critères.</div>
            {/* Mobile: distinguishes "no match" from "truly empty". */}
            <div className="md:hidden text-center py-12 px-4">
              {hasActiveFilters ? (
                <>
                  <p className="text-sm font-medium text-slate-700">Aucun prospect trouvé</p>
                  <p className="text-sm text-slate-500 mt-1">Essayez de modifier vos filtres ou votre recherche.</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={resetFilters} data-testid="btn-reset-filters-empty">
                    Réinitialiser les filtres
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-slate-700">Aucun prospect pour le moment</p>
                  {canCreateProspects && (
                    <Button
                      className="mt-4 gap-2"
                      onClick={() => navigate("/prospects/nouveau")} data-testid="btn-prospects-new-empty"
                    >
                      <Plus size={16} /> Ajouter un prospect
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pharmacie</TableHead>
                  <TableHead className="hidden md:table-cell">Région</TableHead>
                  <TableHead className="hidden lg:table-cell">Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="hidden lg:table-cell">Commercial</TableHead>
                  <TableHead className="hidden xl:table-cell">Dernière activité</TableHead>
                  {hasAnyRowAction && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((p) => {
                  const commercialName = userName(p.assigned_to);
                  return (
                    <TableRow
                      key={p.id}
                      data-testid={`row-prospect-${p.id}`}
                      className="cursor-pointer"
                      onClick={() => navigate(`/prospects/${p.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600`}>
                            <JamoraIcon size={20} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{p.pharmacy_name}</div>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <span>#{p.id.slice(0, 8)}</span>
                              <PilotTag status={p.pilot_status} testid={`pilot-tag-${p.id}`} />
                              <CustomerMark badge={customerBadges[p.id]} />
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600">
                        {(p.commune || p.region) ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={13} className="text-slate-400" />
                            {[p.commune, p.region].filter(Boolean).join(", ")}
                          </span>
                        ) : "Non renseignée"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-600">
                        <div className="text-sm">{p.reference_name || p.pharmacist_name || "Non renseigné"}</div>
                        <div className="text-xs text-slate-400 flex flex-col">
                          {p.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{p.phone}</span>}
                          {p.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{p.email}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${p.status}`}>
                          {STAGE_LABEL[p.status]}
                        </span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {p.assigned_to ? (
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-2xs font-semibold flex-shrink-0 ${avatarColorClass(p.assigned_to)}`}>
                              {getInitials(commercialName)}
                            </div>
                            <span className="text-sm text-slate-700 truncate">{commercialName || "Utilisateur"}</span>
                          </div>
                        ) : <span className="text-slate-400 text-sm">Non assigné</span>}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-slate-500 text-sm">
                        {formatRelativeDate(p.last_activity_at)}
                      </TableCell>
                      {hasAnyRowAction && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions pour ${p.pharmacy_name || "ce prospect"}`} data-testid={`btn-prospect-actions-${p.id}`}>
                                <MoreHorizontal size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">{prospectActionItems(p)}</DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="md:hidden p-2 space-y-2">
            {paged.map((p) => (
              <ProspectCard
                key={p.id} p={p} navigate={navigate} userName={userName} customerBadge={customerBadges[p.id]}
                hasAnyRowAction={hasAnyRowAction} actionItems={prospectActionItems(p)}
              />
            ))}
          </div>
          </>
        )}
      </Card>

      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filtered.length)} sur {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              aria-label="Page précédente"
              data-testid="btn-prospects-prev-page"
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="w-8 h-8 rounded-md bg-jamora text-white flex items-center justify-center text-xs font-medium">
              {currentPage}
            </span>
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
              aria-label="Page suivante"
              data-testid="btn-prospects-next-page"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Modifier */}
      <Dialog open={!!editingProspect} onOpenChange={(open) => !open && setEditingProspect(null)}>
        <DialogContent>
          {editingProspect && (
            <>
              <DialogHeader>
                <DialogTitle>Modifier {editingProspect.pharmacy_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nom de la pharmacie</Label>
                    <Input
                      data-testid="edit-pharmacy-name"
                      value={editForm.pharmacy_name}
                      onChange={(e) => setEditForm((f) => ({ ...f, pharmacy_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Téléphone</Label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email</Label>
                    <Input
                      value={editForm.email}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Région</Label>
                    <Input
                      value={editForm.region}
                      onChange={(e) => setEditForm((f) => ({ ...f, region: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Commune</Label>
                    <Input
                      value={editForm.commune}
                      onChange={(e) => setEditForm((f) => ({ ...f, commune: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Contact principal</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Nom</Label>
                      <Input
                        value={editForm.reference_name}
                        onChange={(e) => setEditForm((f) => ({ ...f, reference_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Fonction</Label>
                      <Input
                        value={editForm.reference_function}
                        onChange={(e) => setEditForm((f) => ({ ...f, reference_function: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Téléphone</Label>
                      <Input
                        value={editForm.reference_phone}
                        onChange={(e) => setEditForm((f) => ({ ...f, reference_phone: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditingProspect(null)} disabled={savingEdit}>Annuler</Button>
                <Button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  data-testid="btn-save-prospect-edit"
                >
                  {savingEdit ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Enregistrer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assigner à */}
      <Dialog open={!!assigningProspect} onOpenChange={(open) => !open && setAssigningProspect(null)}>
        <DialogContent>
          {assigningProspect && (
            <>
              <DialogHeader>
                <DialogTitle>Assigner {assigningProspect.pharmacy_name}</DialogTitle>
              </DialogHeader>
              <RadioGroup value={assignUserId} onValueChange={setAssignUserId} data-testid="radio-assign-user">
                <label className="flex items-center gap-2 py-1.5 cursor-pointer">
                  <RadioGroupItem value="__none__" />
                  <span className="text-sm text-slate-700">Non assigné</span>
                </label>
                {directory.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <RadioGroupItem value={u.id} />
                    <span className="text-sm text-slate-700">{u.name}</span>
                  </label>
                ))}
              </RadioGroup>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAssigningProspect(null)} disabled={savingAssign}>Annuler</Button>
                <Button
                  onClick={saveAssign}
                  disabled={savingAssign}
                  data-testid="btn-save-assign"
                >
                  {savingAssign ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Assigner
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Changer le statut */}
      <Dialog open={!!statusProspect} onOpenChange={(open) => !open && setStatusProspect(null)}>
        <DialogContent>
          {statusProspect && (
            <>
              <DialogHeader>
                <DialogTitle>Changer le statut de {statusProspect.pharmacy_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Statut actuel</Label>
                  <div className="mt-1.5">
                    <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${statusProspect.status}`}>
                      {STAGE_LABEL[statusProspect.status]}
                    </span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Nouveau statut</Label>
                  <Select value={newStatus} onValueChange={selectNewStatus}>
                    <SelectTrigger className="mt-1.5" data-testid="select-new-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MANUAL_STAGES.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <StatusExtraFields status={newStatus} value={statusExtra} onChange={setStatusExtra} />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setStatusProspect(null)} disabled={savingStatus}>Annuler</Button>
                <Button
                  onClick={saveStatus}
                  disabled={savingStatus}
                  data-testid="btn-confirm-status"
                >
                  {savingStatus ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Confirmer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Ajouter une interaction */}
      <Dialog open={!!interactionProspect} onOpenChange={(open) => !open && setInteractionProspect(null)}>
        <DialogContent>
          {interactionProspect && (
            <>
              <DialogHeader>
                <DialogTitle>Ajouter une interaction : {interactionProspect.pharmacy_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={interactionKind} onValueChange={setInteractionKind}>
                    <SelectTrigger className="mt-1.5" data-testid="select-interaction-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INTERACTION_TYPES.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Compte-rendu</Label>
                  <Textarea
                    data-testid="input-interaction-content"
                    rows={4}
                    placeholder="Ce qui s'est passé…"
                    value={interactionContent}
                    onChange={(e) => setInteractionContent(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setInteractionProspect(null)} disabled={savingInteraction}>Annuler</Button>
                <Button
                  onClick={saveInteraction}
                  disabled={savingInteraction}
                  data-testid="btn-save-interaction"
                >
                  {savingInteraction ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Ajouter
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Créer une tâche */}
      <TaskFormSheet
        open={!!taskProspect} onOpenChange={(open) => !open && setTaskProspect(null)} task={null}
        canScopeAll={canScopeAllTasks} canEdit={hasPermission(user, "tasks:edit")} directory={directory}
        onSaved={() => { setTaskProspect(null); load(); }}
        lockedProspect={taskProspect ? { id: taskProspect.id, pharmacy_name: taskProspect.pharmacy_name } : null}
      />

      <DeleteWithReasonDialog
        target={deleting} testId="delete-prospect" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title={`Supprimer « ${deleting?.pharmacy_name || "ce prospect"} » ?`}
        description="La fiche va dans la corbeille : vous pourrez la restaurer pendant 30 jours. Elle ne se supprime pas tant qu'un pilote, une mission, un ticket, un dossier client, un document, du matériel, une formation ou une tâche y est lié."
      />
    </div>
  );
}
